import { Typography, SegmentedButtons } from '@scale/scaleui';
import { useCallback, useMemo } from 'react';
import SuggestionsMark from 'react-scale/components/scalers/tasks/ThreadedInteraction/ResponseSuggestions/components/SuggestionsMark';
import { AutoraterResponse } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ResponseSuggestions/hooks/useResponseSuggestions';

interface LikertScaleProps {
  minLikert?: number;
  maxLikert?: number;
  selectedLikertValue: number | null;
  onSelect: (value: string) => void;
  readOnly: boolean;
  customLabels?: string[];
  suggestions?: AutoraterResponse;
  suggestionsLoading?: boolean;
}

const NO_PREFERENCE = 'No preference';
const DISTANCE_ONE = '{better} is slightly better';
const DISTANCE_TWO = '{better} is better';
const DISTANCE_THREE = '{better} is much better';

export const getDefaultLikerLabelForDistance = (distance: number) => {
  return distance === 0
    ? NO_PREFERENCE
    : distance === 1
    ? DISTANCE_ONE
    : distance === 2
    ? DISTANCE_TWO
    : DISTANCE_THREE;
};

export const LikertScale: React.FC<LikertScaleProps> = ({
  minLikert = 1,
  maxLikert = 7,
  selectedLikertValue,
  onSelect,
  readOnly,
  customLabels,
  suggestions,
  suggestionsLoading,
}) => {
  const likertValues = Array.from({ length: maxLikert - minLikert + 1 }, (_, i) => i + minLikert);

  const medianLikert = useMemo(() => {
    return (maxLikert - minLikert) / 2 + minLikert;
  }, [minLikert, maxLikert]);

  const hasNoPreference = (maxLikert - minLikert) % 2 === 0;

  const getLabelForDistance = useCallback(
    (distance: number, better: string, worse: string) => {
      const replaceVariables = (label: string, better: string, worse: string) => {
        return label.replace('{better}', better).replace('{worse}', worse);
      };

      const distanceLabel = customLabels?.at(distance);

      if (distanceLabel) {
        return replaceVariables(distanceLabel, better, worse);
      }

      return replaceVariables(getDefaultLikerLabelForDistance(distance), better, worse);
    },
    [customLabels],
  );

  const showLikertSuggestion =
    suggestionsLoading ||
    (suggestions?.likertScore &&
      suggestions?.likertScore >= minLikert &&
      suggestions.likertScore <= maxLikert);

  return (
    <div className="flex flex-col gap-4 mt-3 mb-6">
      <Typography.Subtitle3 className="flex items-center gap-2">
        Please select a score to indicate which response is better.
      </Typography.Subtitle3>
      <div className="flex flex-col gap-2">
        <SegmentedButtons
          disabled={readOnly}
          className="w-full justify-between flex flex-row"
          selected={selectedLikertValue === null ? '' : selectedLikertValue.toString()}
          onSelect={onSelect}
          // @ts-ignore
          buttons={likertValues.map(value => {
            const getLabelForLikertValue = (value: number) => {
              const isBetter = value < medianLikert;
              const betterIndex = isBetter ? 'Response 1' : 'Response 2';
              const worseIndex = isBetter ? 'Response 2' : 'Response 1';
              const distance = Math.floor(Math.abs(value - medianLikert));

              return getLabelForDistance(
                hasNoPreference ? distance : distance + 1,
                betterIndex,
                worseIndex,
              );
            };
            return {
              id: value.toString(),
              label: (
                <div className="flex flex-col gap-1">
                  <Typography.Subtitle3>{value.toString()}</Typography.Subtitle3>
                  <Typography.Subtitle4>{getLabelForLikertValue(value)}</Typography.Subtitle4>
                </div>
              ),
              'data-testid': `likert-selection-${value}`,
            };
          })}
        />
        {showLikertSuggestion && (
          <SuggestionsMark
            loading={suggestionsLoading}
            minValue={minLikert}
            maxValue={maxLikert}
            selectedValue={suggestions?.likertScore}
          />
        )}
      </div>
    </div>
  );
};
